import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import MuiCheckbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { ArrowClockwise } from "@phosphor-icons/react/dist/icons/ArrowClockwise";
import { Users } from "@phosphor-icons/react/Users";
import { paths } from "config/paths";
import { FormikProps } from "formik";
import RouterLink from "next/link";
import { v4 } from "uuid";
import * as yup from "yup";
import { ButtonCancel } from "~ui-components/components/atoms/button-cancel";
import {
	ColumnDef,
	DataTable,
} from "~ui-components/components/atoms/data-table";
import { Option } from "~ui-components/components/atoms/option";
import { UserGroup } from "~ui-components/types/__generated/gql/graphql";
import { useWebRouter } from "~utils/next-router/use-web-router";
import { GroupAccessSchema } from "../dataset-access/dataset-access-form";

export const MAP_ACCESS_ANYONE = "ANYONE";
export const MAP_ACCESS_STANDALONE = "STANDALONE";
export const MAP_ACCESS_HIDDEN = "HIDDEN";
export const MAP_ACCESS_PASSWORD = "PASSWORD";
export const MAP_EMBED_LOAD_DEFAULT = "DEFAULT";
export const MAP_EMBED_LOAD_CURRENT = "CURRENT";

export const MapAccessSchema = yup.object({
	isPublic: yup.boolean().required(),
	groupsAccesses: yup.array().of(GroupAccessSchema).required(),
	publicAccessConfigs: yup
		.object({
			type: yup
				.string()
				.oneOf([
					MAP_ACCESS_ANYONE,
					MAP_ACCESS_STANDALONE,
					MAP_ACCESS_HIDDEN,
					MAP_ACCESS_PASSWORD,
				])
				.required(),
			password: yup.string().when("type", {
				is: MAP_ACCESS_PASSWORD,
				then: (schema) => schema.required(),
				otherwise: (schema) => schema.notRequired(),
			}),
			hiddenId: yup.string().when("type", {
				is: MAP_ACCESS_HIDDEN,
				then: (schema) => schema.required(),
				otherwise: (schema) => schema.notRequired(),
			}),
		})
		.when("isPublic", {
			is: true,
			then: (schema) => schema.required(),
			otherwise: (schema) => schema.notRequired(),
		}),
	embedConfigs: yup
		.object({
			loadType: yup
				.string()
				.oneOf([MAP_EMBED_LOAD_DEFAULT, MAP_EMBED_LOAD_CURRENT])
				.required(),
			mapConfigs: yup.string().when("loadType", {
				is: MAP_EMBED_LOAD_CURRENT,
				then: (schema) => schema.required(),
				otherwise: (schema) => schema.notRequired(),
			}),
		})
		.required(),
});

export type MapAccessFormValues = yup.InferType<typeof MapAccessSchema>;

export interface MapAccessFormProps {
	form: FormikProps<MapAccessFormValues>;
	groups: UserGroup[];
	isGroupsLoading: boolean;
	isManageGroupAllowed: boolean;
}

export function MapAccessForm(props: MapAccessFormProps) {
	const { form } = props;

	return (
		<form onSubmit={form.handleSubmit}>
			<CardContent>
				<Stack
					divider={<Divider />}
					spacing={4}>
					<Stack spacing={3}>
						<Grid
							container
							spacing={3}>
							<Grid size={12}>
								<PublicAccess {...props} />
							</Grid>
							<Grid size={12}>
								<InternalAccess {...props} />
							</Grid>
						</Grid>
					</Stack>
				</Stack>
			</CardContent>
			<CardActions sx={{ justifyContent: "flex-end" }}>
				<ButtonCancel />
				<Button
					type="submit"
					variant="contained">
					Save changes
				</Button>
			</CardActions>
		</form>
	);
}

const PublicAccess = (props: MapAccessFormProps) => {
	const { form } = props;

	return (
		<Stack gap={2}>
			<Stack>
				<Typography variant="overline">public access</Typography>
				<Typography variant="caption">
					Enable this setting to make your data publicly viewable and
					downloadable
				</Typography>
				<Box
					display="flex"
					gap={2}
					alignItems="center">
					<Switch
						{...form.getFieldProps("isPublic")}
						checked={form.getFieldProps("isPublic").value}
						onChange={(e, checked) => {
							form.getFieldProps("isPublic").onChange(e);
							if (!checked)
								void form.setFieldValue("publicAccessConfigs", {
									type: MAP_ACCESS_ANYONE,
								});
							if (checked && !form.values.publicAccessConfigs)
								void form.setFieldValue("publicAccessConfigs", {
									type: MAP_ACCESS_ANYONE,
								});
						}}
					/>
					<Typography variant="body2">Public access</Typography>
				</Box>
			</Stack>
			<FormControl>
				<InputLabel>Public access type</InputLabel>
				<Select
					sx={{ width: "200px" }}
					disabled={!form.values.isPublic}
					{...form.getFieldProps("publicAccessConfigs.type")}
					value={
						form.getFieldProps("publicAccessConfigs.type").value ??
						MAP_ACCESS_ANYONE
					}
					onChange={(e) => {
						form.getFieldProps("publicAccessConfigs.type").onChange(e);
						if (
							e.target.value === MAP_ACCESS_HIDDEN &&
							!form.values.publicAccessConfigs.hiddenId
						) {
							void form.setFieldValue("publicAccessConfigs.hiddenId", v4());
						}
						if (e.target.value === MAP_ACCESS_HIDDEN) {
							void form.setFieldValue("groupsAccesses", []);
						}
					}}>
					<Option value={MAP_ACCESS_ANYONE}>Anyone</Option>
					<Option value={MAP_ACCESS_STANDALONE}>Standalone</Option>
					<Option value={MAP_ACCESS_HIDDEN}>Hidden</Option>
					<Option value={MAP_ACCESS_PASSWORD}>Password</Option>
				</Select>
				{form.values.publicAccessConfigs?.type === MAP_ACCESS_ANYONE && (
					<FormHelperText>Anyone can access this map</FormHelperText>
				)}
				{form.values.publicAccessConfigs?.type === MAP_ACCESS_STANDALONE && (
					<FormHelperText>
						Anyone with the link can access this map. Not hidden fom portal
					</FormHelperText>
				)}
				{form.values.publicAccessConfigs?.type === MAP_ACCESS_HIDDEN && (
					<FormHelperText>
						Hidden maps can be viewed by anyone with the link, but only the
						account owner and map creator can manage them.
					</FormHelperText>
				)}
				{form.values.publicAccessConfigs?.type === MAP_ACCESS_PASSWORD && (
					<FormHelperText>
						Anyone with the link can access this map. The map password is
						required to view.
					</FormHelperText>
				)}
			</FormControl>
			{form.values.isPublic &&
				form.values.publicAccessConfigs?.type === MAP_ACCESS_PASSWORD && (
					<FormControl>
						<InputLabel>Map password</InputLabel>
						<OutlinedInput
							sx={{ width: "200px" }}
							{...form.getFieldProps("publicAccessConfigs.password")}
							value={
								form.getFieldProps("publicAccessConfigs.password").value ?? ""
							}
							error={
								!!form.touched.publicAccessConfigs?.password &&
								!!form.errors.publicAccessConfigs?.password
							}
						/>
						{!!form.touched.publicAccessConfigs?.password &&
							!!form.errors.publicAccessConfigs?.password && (
								<FormHelperText error>
									{form.errors.publicAccessConfigs?.password}
								</FormHelperText>
							)}
					</FormControl>
				)}
			{form.values.isPublic &&
				form.values.publicAccessConfigs?.type === MAP_ACCESS_HIDDEN && (
					<Button
						variant="outlined"
						sx={{ width: "200px" }}
						startIcon={<ArrowClockwise />}
						onClick={() =>
							void form.setFieldValue("publicAccessConfigs.hiddenId", v4())
						}>
						Regenerate link
					</Button>
				)}
		</Stack>
	);
};

const InternalAccess = (props: MapAccessFormProps) => {
	const { form, groups, isGroupsLoading } = props;

	const columns = [
		{
			formatter: (row) => (
				<div>
					<Typography
						color="text.primary"
						sx={{ whiteSpace: "nowrap" }}
						variant="subtitle2">
						{row.name}
					</Typography>
				</div>
			),
			name: "Access groups",
		},
		{
			formatter: (row) => {
				const currentGroupAccessIndex = form.values.groupsAccesses.findIndex(
					(g) => g.id === row.id,
				);

				return (
					<MuiCheckbox
						checked={
							(!!form.values.isPublic &&
								form.values.publicAccessConfigs?.type !== MAP_ACCESS_HIDDEN) ||
							!!form.values.groupsAccesses[currentGroupAccessIndex]?.editable ||
							!!form.values.groupsAccesses[currentGroupAccessIndex]?.viewable
						}
						disabled={
							form.values.publicAccessConfigs?.type === MAP_ACCESS_HIDDEN ||
							!!form.values.isPublic ||
							!!form.values.groupsAccesses[currentGroupAccessIndex]?.editable
						}
						onChange={(_, checked) => {
							if (currentGroupAccessIndex === -1)
								void form.setFieldValue("groupsAccesses", [
									...form.values.groupsAccesses,
									{
										id: row.id,
										viewable: checked,
										editable: false,
									},
								]);
							else {
								void form.setFieldValue(
									"groupsAccesses",
									form.values.groupsAccesses.map((g) =>
										g.id === row.id
											? {
													...g,
													viewable: checked,
												}
											: g,
									),
								);
							}
						}}
					/>
				);
			},
			name: "View",
		},
		{
			formatter: (row) => {
				const currentGroupAccessIndex = form.values.groupsAccesses.findIndex(
					(g) => g.id === row.id,
				);

				return (
					<MuiCheckbox
						checked={
							!!form.values.groupsAccesses[currentGroupAccessIndex]?.editable
						}
						disabled={
							form.values.publicAccessConfigs?.type === MAP_ACCESS_HIDDEN
						}
						onChange={(_, checked) => {
							if (currentGroupAccessIndex === -1)
								void form.setFieldValue("groupsAccesses", [
									...form.values.groupsAccesses,
									{
										id: row.id,
										viewable: false,
										editable: checked,
									},
								]);
							else {
								void form.setFieldValue(
									"groupsAccesses",
									form.values.groupsAccesses.map((g) =>
										g.id === row.id
											? {
													...g,
													editable: checked,
												}
											: g,
									),
								);
							}
						}}
					/>
				);
			},
			name: "Edit",
		},
	] as ColumnDef<UserGroup>[];

	return (
		<Stack>
			<Typography variant="overline">internal access</Typography>
			<Card>
				{isGroupsLoading ? (
					<Box
						m={4}
						textAlign="center">
						<CircularProgress />
					</Box>
				) : (
					<>
						<DataTable<UserGroup>
							uniqueRowId={(row) => row.id}
							columns={columns}
							rows={groups}
						/>
						{!groups.length && <EmptyAccess {...props} />}
					</>
				)}
			</Card>
		</Stack>
	);
};

const EmptyAccess = ({ isManageGroupAllowed }: MapAccessFormProps) => {
	const router = useWebRouter();

	return (
		<Stack
			m={4}
			gap={2}
			alignItems="center">
			<Chip label={<Users size="16px" />} />
			<Typography variant="h6">Nothing to see here</Typography>
			<Typography variant="body2">
				No groups yet! Create your first group to start organizing users and
				collaborating with ease
			</Typography>
			{isManageGroupAllowed && (
				<div style={{ textWrap: "nowrap" }}>
					<Button
						component={RouterLink}
						variant="contained"
						href={paths.accountName.groups.create(router._query.accountName)}>
						Create a Group
					</Button>
				</div>
			)}
		</Stack>
	);
};
